import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Download, Copy, Check, Mail, MapPin } from "lucide-react";
import USMap from "@/components/USMap";
import { toast } from "sonner";

const PRESS_PHOTO_FALLBACK_1 = "https://images.unsplash.com/photo-1777245397587-a4bb478a739f?w=1600";
const PRESS_PHOTO_FALLBACK_2 = "https://images.unsplash.com/photo-1628531832865-989e137150ce?w=1600";
const LOGO = "/logo.webp";
const DEFAULT_CONTACT = "50statessober@gmail.com";

function StatCard({ label, value, accent }) {
  return (
    <div className="border-l-2 pl-5 py-1" style={{ borderColor: accent }}>
      <div className="font-serif-display text-5xl md:text-6xl leading-none" style={{ color: accent }}>
        {value.toLocaleString()}
      </div>
      <div className="font-mono-acc text-[10px] tracking-[0.22em] text-[var(--ink-soft)] uppercase mt-3">{label}</div>
    </div>
  );
}

export default function Press() {
  const [stats, setStats] = useState({ total_people: 0, total_days: 0, states_count: 0 });
  const [destinations, setDestinations] = useState([]);
  const [press, setPress] = useState({ mission: "", photo1: "", photo2: "", contact_email: DEFAULT_CONTACT });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/sobriety/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    api.get("/press").then((r) => setPress(r.data)).catch(() => {});
  }, []);

  const contactEmail = press.contact_email || DEFAULT_CONTACT;
  const photo1 = press.photo1 || PRESS_PHOTO_FALLBACK_1;
  const photo2 = press.photo2 || PRESS_PHOTO_FALLBACK_2;
  const headline = `${stats.total_people.toLocaleString()} people · ${stats.total_days.toLocaleString()} days · ${stats.states_count} states`;

  const copyHeadline = () => {
    navigator.clipboard.writeText(`50 States Sober — ${headline}. Sobriety is possible. Ask me how. https://50statessober.com`);
    setCopied(true);
    toast.success("Press headline copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="press-page">
      {/* HERO */}
      <section className="border-b border-[var(--line)]" style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline mb-5">Press kit</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mb-6">
            For journalists,<br />
            <em className="italic text-[var(--forest)]">storytellers, and partners.</em>
          </h1>
          <p className="text-[var(--ink-soft)] text-lg max-w-2xl leading-relaxed">
            Everything you need to write about, link to, or invite us on your show — in one place.
            Press, interview, and partnership inquiries welcome.
          </p>
        </div>
      </section>

      {/* LIVE STATS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-4">The headline</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mb-5 leading-tight">
              A growing number,<br /><em className="italic text-[var(--forest)]">updated live.</em>
            </h2>
            <p className="text-[var(--ink-soft)] leading-relaxed mb-6">
              These three numbers refresh every time someone adds themselves to our Wall of Hope.
              Use them as a hook, a headline, or a live data point in your story.
            </p>
            <button data-testid="press-copy-headline" onClick={copyHeadline}
              className="inline-flex items-center gap-2 text-sm border border-[var(--ink)] px-5 py-2.5 rounded-full hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy press headline"}
            </button>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <StatCard label="People sober" value={stats.total_people} accent="var(--ink)" />
            <StatCard label="Days combined" value={stats.total_days} accent="var(--terracotta)" />
            <StatCard label="States reached" value={stats.states_count} accent="var(--forest)" />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="border-y border-[var(--line)]" style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="overline mb-4">The mission</div>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-6">One paragraph for your draft</h2>
          <div className="border-l-4 border-[var(--terracotta)] pl-6 italic text-lg text-[var(--ink-soft)] leading-relaxed" data-press-mission>
            {press.mission}
          </div>
          <button data-testid="press-copy-mission" onClick={() => { navigator.clipboard.writeText(press.mission); toast.success("Copied"); }}
            className="mt-6 inline-flex items-center gap-2 text-xs border border-[var(--ink)] px-4 py-2 rounded-full hover:bg-[var(--ink)] hover:text-[var(--bg)] transition">
            <Copy className="w-3.5 h-3.5" /> Copy paragraph
          </button>
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="overline mb-4">The map</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mb-3">Where the movement has reached</h2>
        <p className="text-[var(--ink-soft)] mb-10 max-w-xl">Each filled state has at least one person on our Wall of Hope. Tour stops listed below.</p>

        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 bg-[var(--bg-alt)] p-6 rounded-sm">
            <USMap />
          </div>
          <div className="md:col-span-4">
            <div className="overline mb-3">Tour stops</div>
            <ul className="space-y-3 text-sm">
              {destinations.length === 0 && <li className="text-[var(--ink-soft)]">Itinerary coming soon.</li>}
              {destinations.map((d) => (
                <li key={d.id} className="flex items-center gap-3 border-b border-[var(--line)] pb-3">
                  <MapPin className={`w-3.5 h-3.5 shrink-0 ${d.status === "current" ? "text-[var(--forest)]" : d.status === "upcoming" ? "text-[var(--forest)]" : "text-[var(--ink-soft)]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.location} <span className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] ml-1">{d.status}</span></div>
                    <div className="font-mono-acc text-[10px] text-[var(--ink-soft)]">{d.start_date} → {d.end_date || "open"}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PHOTOS */}
      <section className="border-t border-[var(--line)]" style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="overline mb-2">Press assets</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">Logos & photos</h2>
            </div>
            <a href={LOGO} download="50-states-sober-logo.webp" data-testid="press-download-logo"
              className="inline-flex items-center gap-2 text-sm bg-[var(--ink)] text-[var(--bg)] px-5 py-2.5 rounded-full hover:bg-[var(--forest)] transition">
              <Download className="w-4 h-4" /> Download logo
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="aspect-square bg-black rounded-sm flex items-center justify-center p-10">
              <img src={LOGO} alt="50 States Sober logo" className="max-w-full max-h-full" />
            </div>
            <div className="aspect-square overflow-hidden rounded-sm">
              <img src={photo1} alt="On the road" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square overflow-hidden rounded-sm">
              <img src={photo2} alt="Hiking together" className="w-full h-full object-cover" />
            </div>
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-5">For higher-resolution photos or specific shots, please email us using the contact below.</p>
        </div>
      </section>

      {/* CONTACT */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <Mail className="w-7 h-7 text-[var(--forest)] mx-auto mb-5" strokeWidth={1.25} />
        <div className="overline mb-3">Contact</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mb-5">Let's tell this story together.</h2>
        <p className="text-[var(--ink-soft)] mb-7 leading-relaxed">
          Interviews, podcast appearances, partnership opportunities, or just to say hi —
          we read every message ourselves.
        </p>
        <a href={`mailto:${contactEmail}?subject=Press%20inquiry%20%E2%80%94%2050%20States%20Sober`} data-testid="press-email-link"
          className="inline-block bg-[var(--terracotta)] text-[var(--ink)] px-7 py-3.5 rounded-full text-sm hover:bg-[var(--terracotta-dark)] transition-colors">
          {contactEmail}
        </a>
        <div className="mt-8 text-sm text-[var(--ink-soft)]">
          Or send a note from inside the platform: <Link to="/messages" className="text-[var(--forest)] underline">Messages</Link>
        </div>
      </section>
    </div>
  );
}
